<template>
<div class="remote-content" ref="contentEl">
<BootstrapModalContent v-if="boundData === undefined">
  <template #title>
    {{ title }}
  </template>
  <template #content>
    <div class="loading" v-if="!error">
      {{ $pgettext("Modal loading message", "Loading") }}
    </div>
    <div v-else>
      {{ $pgettext("Modal loading message", "Could not fetch data.\nPlease reload the page if the error persists.") }}
      <div class="d-none">{{ error }}</div>
    </div>
  </template>
</BootstrapModalContent>
<NestedEntrypoint v-else v-bind="boundData" />
</div>
</template>
<!--<DjangoForm v-else-if="formData" v-bind="formData" />-->

<script setup lang="ts">
import { useAutoPopupData } from '#src/js/autoPopup.mts'
import type { PopupParams } from '#src/js/popupRouteData.mts'
import { usePopupRouteData } from '#src/js/popupRouteData.mts'
import { assertIsNotNull } from '#src/js/utils.mts'
import { ajaxFetch } from 'ajax/js/ajaxFetch.mjs'
import type { AjaxResponseEventData } from 'ajax/vue/ajaxResponseListener.mts'
import { useAjaxResponseListener } from 'ajax/vue/ajaxResponseListener.mts'
import { $pgettext } from 'gettext-congress'
import { runCallbacks } from 'updates/vue/updateConsumer.mts'
import { computed, nextTick, onUnmounted, onUpdated, ref, toRef, unref, watch, watchEffect, } from 'vue'

import BootstrapModalContent from './BootstrapModalContent.vue'
import NestedEntrypoint from './NestedEntrypoint.vue'

import { useFormDataJson } from './forms/FormData.mts'
import { extractRedirects, getActions } from './forms/response.mts'


type Props = {
  title?: string
  once?: boolean
  refetchDelay?: number
  url: string | URL
}

const props = withDefaults(
  defineProps<Props>(),
  {
    title: () => $pgettext('Modal title', 'Loading...'),
    once: true,
    refetchDelay: 4000,
  },
)

const { router } = usePopupRouteData()
const { currentAutoPopup } = useAutoPopupData()

const contentEl = ref<HTMLElement>()
const urlRef = toRef(props, 'url')

const content = ref<string | undefined>()
const error = ref(false)

const { pending, data: _data, ...formData} = useFormDataJson(content)

const boundData = computed(() => {
  const _pending = unref(pending)
  const { layout, form, inlines } = formData
  const _form = unref(form)
  const _content = unref(content)

  console.debug("RemoteContent", _pending, _form, unref(inlines), unref(layout), _content)

  if (_content === undefined) {
    return undefined
  }

  if (_pending) {
    return undefined
  }

  if (!_form) {
    return { layout: _content }
  }

  return { layout, data: { form, inlines }, }
})

useAjaxResponseListener(contentEl, (event: CustomEvent<AjaxResponseEventData>) => {
  console.debug("BootstrapRemoteContent useAjaxResponseListener", event)

  const data = event.detail

  console.debug("Ajax response event", data)
  const { response, actions, timestamp, } = data

  const { redirect } = extractRedirects(actions)
  const _router = unref(router)!

  if (response.redirected) {
    console.debug("Redirect from response.redirected", response.url)
    _router.replace(response.url)
  }

  if (redirect) {
    event.preventDefault()
    const { href, redirectData } = redirect
    const { to } = redirectData ?? {}
    console.debug("Redirect from redirect action", href, redirectData)
    if (href === props.url) {
      doFetch()
    } else if (to) {
      _router.replacePopup(to as PopupParams)
    } else {
      _router.replace(href)
    }
    unref(currentAutoPopup)?.close()
  }

  const closeModalActions = getActions('closeModal', actions)
  if (closeModalActions.length > 0) {
    contentEl.value?.dispatchEvent(new CustomEvent<'close'>('close', { 'bubbles': true, }))
  }
})

watchEffect(
  () => {
    const _elCheck = unref(contentEl) !== undefined
    const _contentCheck = unref(content) !== null

    if (!_elCheck || !_contentCheck || !props.once ) {
      return
    }

    // NB! result of attachBehaviours is only visible if this nextTick code is executed AND the onUpdated code is updated.
    if (!unref(formData.layout)) {
      nextTick(() => {
        runCallbacks(contentEl)
      })
    }
  },
  {
    // NB! We want changes to be reflected in the dom,
    // so we must have flush: 'post' in options.
    flush: 'post',
  },
)

onUpdated(() => {
  // if we have props.once === false, then we should runCallbacks whenever updates occour.
  const _elCheck = unref(contentEl) !== undefined
  const _contentCheck = unref(content) !== undefined
  const _onceCheck = !props.once

  if (!_elCheck || !_contentCheck || !_onceCheck) {
    return
  }

  if (!unref(formData.layout)) {
    nextTick(() => {
      runCallbacks(contentEl)
    })
  }
})

// activeFetch should not be responsive or we mess up watchers
let activeFetch: { url: URL | string } | null = null
let fetchTimeout: ReturnType<typeof setTimeout> | null = null

onUnmounted(() => {
  activeFetch = null
  if (fetchTimeout) {
    clearTimeout(fetchTimeout)
  }
})

async function fetch (url: string | URL) {
  if (activeFetch?.url === url) {
    console.debug('RemoteContentFetch cancelled: allready running', url)
    return
  }

  const currentFetch = { url }
  activeFetch = currentFetch

  if (url === null) {
    // keep the current content so that popups can close with data during the transition
    fetchTimeout = null
  } else {
    // Content-Type should be text/html because if we request AjaxView mixins they will not render the page without otherwise.
    // AjaxForm mixins render forms as json data based on ContentWrapper.
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'text/html; charset=utf-8',
        'X-ContentWrapper': 'vue-modal'
      }
    }
    let response: Response | null = null
    try {
      response = await ajaxFetch(url, options)
    } catch (error)  {
      console.error(error)
    }

    if (activeFetch !== currentFetch) {
      console.debug('RemoteContentFetch result ignored: stale fetch job')
      return
    } else {
      activeFetch = null
    }

    if (fetchTimeout) {
      fetchTimeout = null
    }

    if (response?.ok) {
      const text = await response.text()
      content.value = text
      error.value = false
    } else {
      error.value = true
      content.value = undefined
      delayedFetch(props.refetchDelay)
    }
  }
}

async function delayedFetch (delay: number) {
  if (fetchTimeout !== null) {
    return
  }

  const url = unref(urlRef)
  assertIsNotNull(url)

  fetchTimeout = setTimeout(async () => {
    await fetch(url)
  }, delay)
}

function doFetch() {
  const { url } = props

  if (!url) {
    return
  }

  fetch(url)
}

watch(urlRef, () => {
  doFetch()
}, { immediate: true })

defineExpose({
  refetch: doFetch,
})

</script>
<style scoped>
.remote-content {
  display: contents;
}
</style>
